import React, { useCallback, useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Dialog from "@mui/material/Dialog";
import FormControlLabel from "@mui/material/FormControlLabel";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import CloseIcon from "@mui/icons-material/Close";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import toast from "react-hot-toast";
import {
  getFileTypeFromFile,
  maxPatrolDrawingUploadBytes,
  PATROL_DRAWING_IMAGE_MAX_BYTES,
  PATROL_DRAWING_PDF_MAX_BYTES,
} from "./patrolDrawingMedia.js";
import PatrolDrawingMediaSurface from "./PatrolDrawingMediaSurface.jsx";

/**
 * @param {{
 *   open: boolean,
 *   drawing: object | null,
 *   onClose: () => void,
 *   onSave: (drawingId: string, payload: object) => void | Promise<void>,
 *   existingTitles?: string[],
 * }} props
 */
export default function EditPatrolDrawingDialog({
  open,
  drawing,
  onClose,
  onSave,
  existingTitles = [],
}) {
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    isFavorite: false,
  });
  const [replacementFile, setReplacementFile] = useState(null);
  const [filePreviewUrl, setFilePreviewUrl] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  useEffect(() => {
    if (!open || !drawing) return;
    setFormData({
      title: drawing.title || "",
      description: drawing.description || "",
      isFavorite: Boolean(drawing.isFavorite),
    });
    setReplacementFile(null);
    setSaveError(null);
  }, [open, drawing]);

  useEffect(() => {
    if (!replacementFile) {
      setFilePreviewUrl(null);
      return undefined;
    }
    const url = URL.createObjectURL(replacementFile);
    setFilePreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [replacementFile]);

  const titleError = useMemo(() => {
    const title = formData.title.trim();
    if (!title) return "Name is required.";
    const duplicate = existingTitles.some(
      (t) =>
        t.trim().toLowerCase() === title.toLowerCase() &&
        t.trim().toLowerCase() !== (drawing?.title || "").trim().toLowerCase(),
    );
    return duplicate ? "A drawing with this name already exists." : null;
  }, [formData.title, existingTitles, drawing?.title]);

  const previewDrawing = useMemo(() => {
    if (!drawing) return null;
    if (replacementFile && filePreviewUrl) {
      return {
        fileUrl: filePreviewUrl,
        fileType: getFileTypeFromFile(replacementFile),
        fileName: replacementFile.name,
      };
    }
    return drawing;
  }, [drawing, replacementFile, filePreviewUrl]);

  const handleClose = useCallback(() => {
    setReplacementFile(null);
    onClose();
  }, [onClose]);

  const handleSave = async () => {
    if (!drawing || isSaving || titleError) return;
    setIsSaving(true);
    setSaveError(null);
    try {
      await onSave(drawing.id, {
        title: formData.title.trim(),
        description: formData.description.trim(),
        isFavorite: formData.isFavorite,
        file: replacementFile || undefined,
      });
      toast.success("Drawing updated.");
      handleClose();
    } catch (err) {
      const message =
        err?.message || "Failed to update drawing. Please try again.";
      setSaveError(message);
      toast.error(message);
    } finally {
      setIsSaving(false);
    }
  };

  if (!drawing) return null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: "12px" } }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 3,
          py: 2,
          borderBottom: "1px solid #E5E7EB",
        }}
      >
        <Typography sx={{ fontSize: 18, fontWeight: 600, color: "#101828" }}>
          Edit drawing
        </Typography>
        <IconButton size="small" onClick={handleClose} aria-label="Close">
          <CloseIcon />
        </IconButton>
      </Box>

      <Box sx={{ px: 3, py: 2.5, display: "flex", flexDirection: "column", gap: 2 }}>
        <TextField
          label="Name"
          value={formData.title}
          onChange={(e) =>
            setFormData((prev) => ({ ...prev, title: e.target.value }))
          }
          error={Boolean(titleError)}
          helperText={titleError}
          fullWidth
          size="small"
        />
        <TextField
          label="Description"
          value={formData.description}
          onChange={(e) =>
            setFormData((prev) => ({ ...prev, description: e.target.value }))
          }
          fullWidth
          size="small"
          multiline
          minRows={2}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={formData.isFavorite}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  isFavorite: e.target.checked,
                }))
              }
            />
          }
          label="Favorite"
        />
        <Box>
          <Button
            component="label"
            variant="outlined"
            startIcon={<CloudUploadIcon />}
            sx={{ textTransform: "none", borderRadius: "8px" }}
          >
            Replace file (optional)
            <input
              type="file"
              hidden
              accept=".jpg,.jpeg,.png,.pdf"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  const maxBytes = maxPatrolDrawingUploadBytes(file);
                  if (file.size > maxBytes) {
                    const isPdf = file.name.toLowerCase().endsWith(".pdf");
                    const limitMb = isPdf
                      ? PATROL_DRAWING_PDF_MAX_BYTES / (1024 * 1024)
                      : PATROL_DRAWING_IMAGE_MAX_BYTES / (1024 * 1024);
                    toast.error(
                      `${isPdf ? "PDF" : "Image"} must not exceed ${limitMb}MB.`,
                    );
                    e.target.value = "";
                    return;
                  }
                }
                setReplacementFile(file || null);
                e.target.value = "";
              }}
            />
          </Button>
          {replacementFile ? (
            <Typography sx={{ fontSize: 12, color: "#64748B", mt: 0.5 }}>
              {replacementFile.name}
            </Typography>
          ) : null}
        </Box>
        {previewDrawing ? (
          <Box
            sx={{
              borderRadius: 1,
              overflow: "hidden",
              border: "1px solid #E5E7EB",
              height: 180,
            }}
          >
            <PatrolDrawingMediaSurface
              drawing={previewDrawing}
              minHeight={180}
              compact
            />
          </Box>
        ) : null}
        {saveError ? (
          <Typography sx={{ fontSize: 13, color: "#DC2626" }}>{saveError}</Typography>
        ) : null}
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 1,
          px: 3,
          py: 2,
          bgcolor: "#F9FAFB",
          borderTop: "1px solid #E5E7EB",
        }}
      >
        <Button onClick={handleClose} sx={{ textTransform: "none" }}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          disabled={Boolean(titleError) || isSaving}
          sx={{
            bgcolor: "#2932E5",
            color: "white",
            textTransform: "none",
            borderRadius: "8px",
            "&:hover": { bgcolor: "#1f28b8" },
          }}
        >
          {isSaving ? <CircularProgress size={20} sx={{ color: "#fff" }} /> : "Save"}
        </Button>
      </Box>
    </Dialog>
  );
}
